/**
 * printInFrame — the ONE way in-app documents are sent to the browser printer.
 *
 * Every print renders inside a hidden same-origin iframe. Nothing is injected
 * into the host document except that iframe: no <style>, no body class, no
 * `@media print` hide-all rule. Printing inside the live app page (07-02 →
 * 10-10) let any CSS or leftover node from another feature blank or duplicate
 * the print (TKT-00022, TKT-00084, TKT-00151, TKT-00154, …).
 *
 * The frame gets the app's BASE stylesheets (`appStyles`, default on) because
 * today's printed output is laid out under them (Tailwind preflight, app fonts,
 * theme variables); without them a slip's headings, images and fonts would
 * print differently. Only the entry CSS is copied and every print rule in it
 * is stripped (frameStyles.ts), so the frame's print rules come only from the
 * document's own CSS. `@media print` / `@page` outside src/lib/print is banned
 * by the CSS guard test (src/lib/print/__tests__/printCssGuard.test.ts).
 *
 * Lifecycle:
 * - Waits for the frame's fonts, stylesheets and images (max 5s) before print().
 * - The frame is removed on ITS window's `afterprint`, registered BEFORE
 *   print(): desktop Chrome fires afterprint synchronously inside print().
 *   Safari returns from print() before the dialog closes, so there is no fixed
 *   short removal delay, only a 60s fallback.
 * - Single-flight: a new print removes a still-pending frame first.
 */

import { baseStylesMarkup, escapeStyleText } from "./frameStyles";

const FRAME_ID = "zvg-print-frame";
const READY_TIMEOUT_MS = 5_000;
const FALLBACK_REMOVE_MS = 60_000;

export interface PrintInFrameOptions {
  /** Document markup; parsed like innerHTML (its own <style> tags apply). */
  html: string;
  /** The document's CSS, applied after the app stylesheets. */
  css?: string;
  /** `@page` rules for this document only. */
  pageCss?: string;
  /** Frame title (print header / Save-as-PDF name). Defaults to the app's title. */
  title?: string;
  /**
   * Id of the element that wraps `html` in the frame body, so the document's
   * own selectors (e.g. `#shipping-label-print .sl-page`) keep matching.
   */
  wrapperId?: string;
  /** Copy the app's base stylesheets into the frame (see file header). Default true. */
  appStyles?: boolean;
}

/** Removes the currently pending print frame, if any. */
let cancelActive: (() => void) | null = null;

interface TrackedFrame {
  /** Resolves once the frame has been removed. */
  removed: Promise<void>;
  /** Removes this frame now (idempotent; never touches a newer print). */
  remove: () => void;
}

/**
 * Removes `frame` after its print is over (afterprint or 60s fallback) and
 * makes it the single active print, removing any still-pending one first.
 */
function trackFrame(frame: HTMLIFrameElement, onRemoved?: () => void): TrackedFrame {
  cancelActive?.();
  const win = frame.contentWindow;
  let done = false;
  let resolveRemoved: () => void = () => {};
  const removed = new Promise<void>((resolve) => {
    resolveRemoved = resolve;
  });
  const remove = () => {
    if (done) return;
    done = true;
    clearTimeout(fallback);
    win?.removeEventListener("afterprint", remove);
    frame.remove();
    if (cancelActive === remove) cancelActive = null;
    onRemoved?.();
    resolveRemoved();
  };
  const fallback = setTimeout(remove, FALLBACK_REMOVE_MS);
  win?.addEventListener("afterprint", remove, { once: true });
  cancelActive = remove;
  return { removed, remove };
}

function createHiddenFrame(): HTMLIFrameElement {
  const frame = document.createElement("iframe");
  frame.id = FRAME_ID;
  frame.setAttribute("aria-hidden", "true");
  frame.tabIndex = -1;
  Object.assign(frame.style, {
    position: "fixed",
    right: "0",
    bottom: "0",
    width: "0",
    height: "0",
    border: "0",
  });
  document.body.appendChild(frame);
  return frame;
}

const escapeAttr = (value: string) =>
  value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

/** <html> attributes that carry theme state (dark class, CSS variables, lang). */
function htmlAttributes(): string {
  const root = document.documentElement;
  return ["class", "style", "lang", "dir"]
    .map((name) => {
      const value = root.getAttribute(name);
      return value ? ` ${name}="${escapeAttr(value)}"` : "";
    })
    .join("");
}

function writeFrameDocument(frame: HTMLIFrameElement, options: PrintInFrameOptions): Document {
  const doc = frame.contentDocument;
  if (!doc) throw new Error("Print frame has no document");
  const { css = "", pageCss = "", title = document.title, wrapperId, appStyles = true } = options;
  doc.open();
  doc.write(
    [
      `<!DOCTYPE html><html${appStyles ? htmlAttributes() : ""}><head><meta charset="utf-8">`,
      `<title>${escapeAttr(title)}</title>`,
      appStyles ? baseStylesMarkup() : "",
      css ? `<style>${escapeStyleText(css)}</style>` : "",
      pageCss ? `<style>${escapeStyleText(pageCss)}</style>` : "",
      `</head><body><div${wrapperId ? ` id="${escapeAttr(wrapperId)}"` : ""}></div></body></html>`,
    ].join(""),
  );
  doc.close();
  // innerHTML (not document.write) so the markup parses exactly as it did when
  // it was injected into the app page: stray <html>/<body> tags are dropped.
  const wrapper = doc.body.firstElementChild as HTMLElement;
  wrapper.innerHTML = options.html;
  return doc;
}

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function stylesheetLoaded(link: HTMLLinkElement): Promise<void> {
  if (link.sheet) return Promise.resolve();
  return new Promise((resolve) => {
    link.addEventListener("load", () => resolve(), { once: true });
    link.addEventListener("error", () => resolve(), { once: true });
  });
}

/** Fonts, stylesheets and images inside the frame are ready (or 5s passed). */
async function frameReady(doc: Document): Promise<void> {
  const ready = (async () => {
    const links = Array.from(doc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'));
    await Promise.all(links.map(stylesheetLoaded));
    const images = Array.from(doc.images);
    images.forEach((img) => {
      img.loading = "eager";
    });
    void doc.body?.offsetHeight; // lay out so used fonts start loading
    await Promise.all([
      doc.fonts?.ready,
      ...images.map((img) => img.decode?.().catch(() => undefined)),
    ]);
  })();
  await Promise.race([ready, delay(READY_TIMEOUT_MS)]);
}

/**
 * Print `html` in an isolated frame. Resolves when the print is over and the
 * frame is removed (or when a newer print superseded this one).
 */
export async function printInFrame(options: PrintInFrameOptions): Promise<void> {
  cancelActive?.();
  const frame = createHiddenFrame();
  let doc: Document;
  try {
    doc = writeFrameDocument(frame, options);
  } catch (error) {
    frame.remove();
    throw error;
  }
  // Tracked only AFTER the document is written: document.open() erases every
  // listener on the frame's window (HTML spec), afterprint included. Still
  // registered before print().
  const { removed, remove } = trackFrame(frame);
  try {
    await frameReady(doc);
    // Superseded by a newer print while waiting: its frame is already gone.
    if (!frame.isConnected) return removed;
    frame.contentWindow?.print();
  } catch (error) {
    remove();
    throw error;
  }
  return removed;
}

/**
 * Print a frame the caller already loaded (e.g. a PDF blob) with the same
 * lifecycle: afterprint-based removal, 60s fallback, single-flight.
 */
export function printLoadedFrame(frame: HTMLIFrameElement, onRemoved?: () => void): Promise<void> {
  const { removed, remove } = trackFrame(frame, onRemoved);
  try {
    frame.contentWindow?.print();
  } catch (error) {
    remove();
    throw error;
  }
  return removed;
}
